import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Hash, Megaphone, MapPin, Image, Mic, MicOff, Paperclip,
  ArrowLeft, Users, AtSign, Play, Pause, ChevronDown, Check, CheckCheck, X, Plus,
  Volume2, Search, Bell, BellOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API = process.env.REACT_APP_BACKEND_URL;

// ============ VOICE RECORDER HOOK ============
const useVoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.start();
      setIsRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch {
      toast.error('Microphone access denied');
    }
  }, []);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        resolve(null);
        return;
      }
      mediaRecorderRef.current.onstop = () => {
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
        setIsRecording(false);
        resolve({ blob, duration });
      };
      mediaRecorderRef.current.stop();
    });
  }, [duration]);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
      mediaRecorderRef.current.stop();
    }
    clearInterval(timerRef.current);
    setIsRecording(false);
    setDuration(0);
  }, []);

  return { isRecording, duration, startRecording, stopRecording, cancelRecording };
};

// ============ AUDIO PLAYER ============
const AudioPlayer = ({ path, durationSec }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!path) return;
    const tk = localStorage.getItem('token') || localStorage.getItem('crewToken');
    const fetchAudio = async () => {
      try {
        const res = await axios.get(`${API}/api/files/${path}`, {
          headers: { Authorization: `Bearer ${tk}` },
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data);
        blobUrlRef.current = url;
        setBlobUrl(url);
      } catch (err) { /* audio fetch failed */ }
    };
    fetchAudio();
    return () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); };
  }, [path]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); }
    else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-2 bg-stone-100 rounded-full px-3 py-1.5 min-w-[160px]">
      {blobUrl && (
        <audio
          ref={audioRef}
          src={blobUrl}
          onTimeUpdate={() => setProgress(audioRef.current?.currentTime || 0)}
          onEnded={() => { setPlaying(false); setProgress(0); }}
        />
      )}
      <button onClick={toggle} className="text-amber-600 hover:text-amber-700" data-testid="audio-play-btn">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <div className="flex-1 h-1.5 bg-stone-300 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-500 rounded-full transition-all"
          style={{ width: `${durationSec ? (progress / durationSec) * 100 : 0}%` }}
        />
      </div>
      <span className="text-xs text-stone-500 min-w-[32px]">
        {playing ? formatTime(progress) : formatTime(durationSec || 0)}
      </span>
    </div>
  );
};

// ============ MENTION DROPDOWN ============
const MentionDropdown = ({ members, filter, onSelect, position }) => {
  const filtered = members.filter(m =>
    m.name.toLowerCase().includes(filter.toLowerCase())
  );
  if (!filtered.length) return null;
  return (
    <div
      className="absolute z-50 bg-white border border-stone-200 rounded-lg shadow-lg max-h-40 overflow-y-auto w-56"
      style={{ bottom: position?.bottom || 50, left: position?.left || 0 }}
    >
      {filtered.map(m => (
        <button
          key={m.id}
          onClick={() => onSelect(m)}
          className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-center gap-2 text-sm"
        >
          <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center text-xs font-bold text-amber-800">
            {m.name.charAt(0)}
          </div>
          <span>{m.name}</span>
          <span className="text-xs text-stone-400 ml-auto">{m.role}</span>
        </button>
      ))}
    </div>
  );
};

// ============ MESSAGE BUBBLE ============
const MessageBubble = ({ msg, isOwn, members }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const photoUrlRef = useRef(null);

  useEffect(() => {
    if (msg.message_type === 'photo' && msg.attachment_path) {
      const tk = localStorage.getItem('token') || localStorage.getItem('crewToken');
      axios.get(`${API}/api/files/${msg.attachment_path}`, {
        headers: { Authorization: `Bearer ${tk}` },
        responseType: 'blob',
      }).then(res => {
        const url = URL.createObjectURL(res.data);
        photoUrlRef.current = url;
        setPhotoUrl(url);
      }).catch(() => {});
    }
    return () => { if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current); };
  }, [msg.attachment_path, msg.message_type]);

  const readCount = Object.keys(msg.read_by || {}).length;
  const time = new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Render text with @mentions highlighted
  const renderText = (text) => {
    if (!text) return null;
    const parts = text.split(/(@\w[\w\s]*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        const name = part.slice(1);
        const isMention = members.some(m => m.name.toLowerCase() === name.toLowerCase());
        if (isMention) return <span key={`mention-${i}-${name}`} className="text-amber-600 font-semibold bg-amber-100 rounded px-0.5">{part}</span>;
      }
      return <span key={`text-${i}`}>{part}</span>;
    });
  };

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2 group`}>
      <div className={`max-w-[75%] ${isOwn ? 'order-1' : ''}`}>
        {!isOwn && (
          <p className="text-xs text-stone-500 mb-0.5 ml-1 font-medium">{msg.author_name}</p>
        )}
        <div className={`rounded-2xl px-3.5 py-2 ${
          isOwn
            ? 'bg-amber-500 text-white rounded-tr-sm'
            : 'bg-white border border-stone-200 text-stone-800 rounded-tl-sm'
        }`}>
          {msg.message_type === 'photo' && photoUrl && (
            <img src={photoUrl} alt="Shared" className="rounded-lg max-w-[280px] max-h-[200px] object-cover mb-1" />
          )}
          {msg.message_type === 'voice' && (
            <AudioPlayer path={msg.attachment_path} durationSec={msg.attachment_duration || 0} />
          )}
          {msg.text && (
            <p className="text-sm whitespace-pre-wrap break-words">{renderText(msg.text)}</p>
          )}
        </div>
        <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end' : 'justify-start'} px-1`}>
          <span className="text-[10px] text-stone-400">{time}</span>
          {isOwn && (
            readCount > 1
              ? <CheckCheck className="w-3 h-3 text-blue-500" />
              : <Check className="w-3 h-3 text-stone-400" />
          )}
        </div>
      </div>
    </div>
  );
};

// ============ MAIN TEAM CHAT ============
export default function TeamChat() {
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [members, setMembers] = useState([]);
  const [myId, setMyId] = useState('');
  const [showMention, setShowMention] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentions, setMentions] = useState([]);
  const [sending, setSending] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const { isRecording, duration, startRecording, stopRecording, cancelRecording } = useVoiceRecorder();
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushNotifications();

  const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
  const headers = { Authorization: `Bearer ${token}` };
  const isCrew = !!localStorage.getItem('crewToken') && !localStorage.getItem('token');

  // Get identity
  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setMyId(payload.type === 'crew' ? payload.sub : (payload.sub || payload.id));
    } catch (err) { /* malformed token */ }
  }, [token]);

  // Fetch channels
  const fetchChannels = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/chat/channels`, { headers });
      setChannels(res.data || []);
    } catch (err) { /* network error */ }
  }, [token]);

  // Auto-select first channel once loaded
  useEffect(() => {
    if (!activeChannel && channels.length) setActiveChannel(channels[0]);
  }, [channels, activeChannel]);

  // Fetch messages for active channel
  const fetchMessages = useCallback(async () => {
    if (!activeChannel || !token) return;
    try {
      const res = await axios.get(`${API}/api/chat/channels/${activeChannel.id}/messages?limit=80`, { headers });
      setMessages(res.data || []);
    } catch (err) { /* network error */ }
  }, [activeChannel?.id, token]);

  // Fetch members for @mentions
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/chat/members`, { headers }).then(r => setMembers(r.data || [])).catch(() => { /* ignore */ });
  }, [token]);

  // Initial load + sync yard channels
  useEffect(() => {
    if (!token) return;
    fetchChannels();
    axios.post(`${API}/api/chat/sync-yard-channels`, {}, { headers }).catch(() => { /* ignore */ });
  }, []);

  // Fetch messages on channel change
  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // Poll for new messages (5s when focused, 30s when background)
  useEffect(() => {
    let interval;
    const setPoll = () => {
      clearInterval(interval);
      const delay = document.hidden ? 30000 : 5000;
      interval = setInterval(() => { fetchMessages(); fetchChannels(); }, delay);
    };
    setPoll();
    document.addEventListener('visibilitychange', setPoll);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', setPoll); };
  }, [fetchMessages, fetchChannels]);

  // Auto-scroll on new messages
  useEffect(() => {
    const box = messagesEndRef.current?.parentElement;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Handle text input with @mention detection
  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const atMatch = before.match(/@(\w*)$/);
    if (atMatch) {
      setShowMention(true);
      setMentionFilter(atMatch[1]);
    } else {
      setShowMention(false);
    }
  };

  const handleMentionSelect = (member) => {
    const cursor = inputRef.current?.selectionStart || text.length;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const atIdx = before.lastIndexOf('@');
    const newText = before.slice(0, atIdx) + `@${member.name} ` + after;
    setText(newText);
    setShowMention(false);
    setMentions(prev => [...prev.filter(m => m.id !== member.id), { id: member.id, name: member.name }]);
    inputRef.current?.focus();
  };

  // Send message
  const sendMessage = async (msgType = 'text', attachPath = null, attachName = null, attachDur = null) => {
    if (msgType === 'text' && !text.trim()) return;
    if (!activeChannel) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/chat/channels/${activeChannel.id}/messages`, {
        text: text.trim(),
        message_type: msgType,
        attachment_path: attachPath,
        attachment_name: attachName,
        attachment_duration: attachDur,
        mentions,
      }, { headers });
      setText('');
      setMentions([]);
      fetchMessages();
      fetchChannels();
    } catch {
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  // Upload photo
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/api/chat/upload`, formData, { headers });
      await sendMessage('photo', res.data.path, file.name);
    } catch {
      toast.error('Upload failed');
    }
    e.target.value = '';
  };

  // Send voice note
  const handleVoiceSend = async () => {
    const result = await stopRecording();
    if (!result) return;
    const formData = new FormData();
    formData.append('file', result.blob, 'voice_note.webm');
    try {
      const res = await axios.post(`${API}/api/chat/upload`, formData, { headers });
      await sendMessage('voice', res.data.path, 'voice_note.webm', result.duration);
    } catch {
      toast.error('Voice upload failed');
    }
  };

  const channelIcon = (ch) => {
    if (ch.type === 'announcements') return <Megaphone className="w-4 h-4 text-purple-500" />;
    if (ch.type === 'yard') return <MapPin className="w-4 h-4 text-green-600" />;
    return <Hash className="w-4 h-4 text-stone-500" />;
  };

  const totalUnread = channels.reduce((sum, ch) => sum + (ch.unread_count || 0), 0);

  const filteredChannels = channels.filter(ch =>
    ch.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-screen flex bg-stone-100 relative overflow-hidden" data-testid="team-chat-page">
      {/* Channel Sidebar */}
      <div className={`${showSidebar ? 'absolute inset-0 z-20 w-full lg:static lg:w-72' : 'w-0 overflow-hidden'} bg-white border-r border-stone-200 flex flex-col transition-all duration-200`}>
        {/* Header */}
        <div className="p-4 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-amber-600" />
            <h2 className="font-bold text-lg text-stone-800">Team Chat</h2>
            {totalUnread > 0 && (
              <span className="ml-auto bg-amber-100 text-amber-600 text-xs font-bold rounded-full px-2 py-0.5">{totalUnread}</span>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="p-2 border-b border-stone-100">
          <div className="flex items-center gap-2 bg-stone-50 rounded-lg px-2.5 py-1.5">
            <Search className="w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search channels..."
              className="bg-transparent text-sm flex-1 outline-none text-stone-700 placeholder:text-stone-400"
              data-testid="channel-search"
            />
          </div>
        </div>

        {/* Channel List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {filteredChannels.map(ch => (
            <button
              key={ch.id}
              onClick={() => { setActiveChannel(ch); if (window.innerWidth < 1024) setShowSidebar(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all ${
                activeChannel?.id === ch.id
                  ? 'bg-amber-100 text-amber-900 font-semibold'
                  : 'text-stone-700 hover:bg-stone-50'
              }`}
              data-testid={`channel-${ch.type}-${ch.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {channelIcon(ch)}
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{ch.name}</p>
                {ch.last_message && (
                  <p className="text-xs text-stone-400 truncate">
                    {ch.last_message.author}: {ch.last_message.text}
                  </p>
                )}
              </div>
              {ch.unread_count > 0 && (
                <span className="bg-amber-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                  {ch.unread_count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Members count */}
        <div className="p-3 border-t border-stone-200">
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <Users className="w-3.5 h-3.5" />
            <span>{members.length} team members</span>
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="h-14 border-b border-stone-200 bg-white flex items-center gap-3 px-4">
          <button onClick={() => setShowSidebar(!showSidebar)} className="lg:hidden text-stone-500">
            <MessageSquare className="w-5 h-5" />
          </button>
          {activeChannel && (
            <>
              {channelIcon(activeChannel)}
              <div>
                <h3 className="font-semibold text-stone-800 text-sm">{activeChannel.name}</h3>
                <p className="text-xs text-stone-400">{activeChannel.description}</p>
              </div>
              {activeChannel.admin_only && (
                <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Admin only</span>
              )}
              {/* Push notification toggle */}
              <div className="ml-auto">
                {pushSupported && (
                  <button
                    onClick={async () => {
                      if (pushSubscribed) {
                        await pushUnsubscribe();
                        toast.success('Push notifications disabled');
                      } else {
                        const ok = await pushSubscribe();
                        if (ok) toast.success('Push notifications enabled! You will be notified of new messages.');
                        else toast.error('Could not enable notifications. Check browser permissions.');
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      pushSubscribed
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                    }`}
                    data-testid="push-toggle-btn"
                  >
                    {pushSubscribed ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                    {pushSubscribed ? 'Notifications On' : 'Enable Notifications'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-stone-400">
              <MessageSquare className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm">No messages yet. Start the conversation!</p>
            </div>
          )}
          {messages.map(msg => (
            <MessageBubble key={msg.id} msg={msg} isOwn={msg.author_id === myId} members={members} />
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        {activeChannel && (
          <div className="border-t border-stone-200 bg-white p-3">
            {isRecording ? (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm text-red-700 font-medium">Recording... {Math.floor(duration / 60)}:{String(duration % 60).padStart(2, '0')}</span>
                <div className="flex-1" />
                <Button variant="ghost" size="sm" onClick={cancelRecording} className="text-stone-500" data-testid="cancel-recording-btn">
                  <X className="w-4 h-4" />
                </Button>
                <Button size="sm" onClick={handleVoiceSend} className="bg-red-500 hover:bg-red-600 text-white" data-testid="send-voice-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-end gap-2">
                <div className="relative flex-1 min-w-0">
                  {showMention && (
                    <MentionDropdown
                      members={members}
                      filter={mentionFilter}
                      onSelect={handleMentionSelect}
                      position={{ bottom: 50, left: 0 }}
                    />
                  )}
                  <div className="flex items-end bg-stone-50 border border-stone-200 rounded-xl px-3 py-2">
                    <textarea
                      ref={inputRef}
                      value={text}
                      onChange={handleTextChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder={
                        activeChannel.admin_only
                          ? "Post an announcement..."
                          : "Type a message... Use @ to mention"
                      }
                      className="flex-1 w-full min-w-0 bg-transparent resize-none outline-none text-sm text-stone-800 placeholder:text-stone-400 min-h-[24px] max-h-[120px]"
                      rows={1}
                      data-testid="chat-input"
                    />
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                <Button
                  variant="ghost" size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-stone-500 hover:text-amber-600 h-10 w-10"
                  data-testid="photo-upload-btn"
                >
                  <Image className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost" size="icon"
                  onClick={startRecording}
                  className="text-stone-500 hover:text-red-500 h-10 w-10"
                  data-testid="voice-record-btn"
                >
                  <Mic className="w-5 h-5" />
                </Button>
                <Button
                  size="icon"
                  onClick={() => sendMessage()}
                  disabled={!text.trim() || sending}
                  className="bg-amber-500 hover:bg-amber-600 text-white h-10 w-10 rounded-xl"
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
